/**
 * Console centrale — panneau d'ENRÔLEMENT des sites
 * @author Dr Hamid MADANI <drmdh@msn.com>
 * @license AGPL-3.0-or-later
 *
 * Simple raccordement de `@mostajs/enrollment-ui-react` aux routes du plan de contrôle.
 *
 * Toute la logique — et surtout les garde-fous — vit dans le module : le code d'invitation n'est
 * affiché qu'une fois, aucun jeton n'est jamais rendu, et le bouton d'approbation est DÉSACTIVÉ
 * tant qu'une alerte de ré-enrôlement n'a pas été explicitement acceptée. Ce fichier ne réimplémente
 * rien de tout cela : il branche des appels HTTP. C'est exactement pourquoi le module existe.
 *
 * Rappel utile : ces garde-fous sont de CONFORT. Le refus qui protège vraiment est celui du
 * service, côté serveur (`reenrolement_non_confirme`) — une IHM qui protège seule ne protège pas,
 * il suffit d'un `curl`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { EnrollmentTab } from '@mostajs/enrollment-ui-react';

const post = (url, body) =>
  fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  }).then((r) => r.json());

export default function EnrollmentPanel() {
  const [data, setData] = useState(null);
  const [revele, setRevele] = useState(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await fetch('/api/enrollment', { credentials: 'same-origin' }).then((x) => x.json());
      if (!r.success) { setErreur(r.error || 'Chargement impossible'); return; }
      setData(r.data);
    } catch (e) {
      setErreur(e.message);
    }
  }, []);

  useEffect(() => {
    charger();
    // Les serveurs en attente re-sondent toutes les 30 s : une demande peut arriver à tout moment,
    // sans action du manager. La liste doit vivre toute seule.
    const t = setInterval(charger, 5000);
    return () => clearInterval(t);
  }, [charger]);

  if (erreur) return <div className="card"><h2>Enrôlement</h2><p className="muted">{erreur}</p></div>;
  if (!data) return <div className="card"><h2>Enrôlement</h2><p className="muted">…</p></div>;

  return (
    <EnrollmentTab
      revealedCode={revele}
      pending={data.pending}
      sites={data.sites}
      onInvite={async (label) => {
        // Le code en clair n'existe QUE dans cette réponse — le serveur ne le redonnera jamais. On
        // le garde donc en mémoire React tant que la page vit : sinon le rafraîchissement suivant
        // (toutes les 5 s) l'effacerait sous les yeux du manager, en pleine dictée au téléphone.
        const r = await post('/api/enrollment/invite', { label });
        if (r.success) setRevele({ display: r.data.display });
        await charger();
      }}
      onApprove={async (requestId, { siteId, name, acceptReenrollment }) => {
        const r = await post('/api/enrollment/approve', { requestId, siteId, name, acceptReenrollment });
        if (!r.success) setErreur(`Refusé : ${r.error}`);
        await charger();
      }}
      onReject={async (requestId) => { await post('/api/enrollment/reject', { requestId }); await charger(); }}
      onRevoke={async (siteId) => { await post('/api/enrollment/revoke', { siteId }); await charger(); }}
    />
  );
}
